// Generated by tutorials/record.cjs from tutorials/articles/h38-intake-studies.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h38 --text-only` (wording) or
// `node tutorials/record.cjs h38` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'intake-studies',
  catalogId: 'h38',
  title: 'Intake studies',
  summary: 'Run an intake study (a calorie count): record how much of each meal a resident eats over one to seven days, then see what they took in against their needs, and print it.',
  audience: 'Dietitians and dietary managers who run calorie counts, and the staff who record them',
  area: 'residents',
  learn: [
    'Find the intake studies that are running',
    'Start a study for a resident',
    'Record how much of each item the resident ate',
    'Read what they took in against their needs',
    'Print the study, or the sheet to record it on',
  ],
  keywords: [
    'intake study',
    'intake studies',
    'calorie count',
    'calorie counts',
    'food intake',
    'percent eaten',
    'meal intake',
    'acceptance',
    'acceptance sheet',
    'intake record',
    'how much eaten',
    'nutrient intake',
    'poor intake',
    'weight loss',
  ],
  steps: [
    {
      id: 'list',
      part: 'Start an intake study',
      heading: 'Open Intake Studies',
      text: 'Under Residents, select Intake Studies.\n\nThe list shows current residents\' studies that are running, about to start, or ended in the last 30 days, newest first. Status says Not started, In progress or Complete. Check Show all to see every study, older ones too.',
      media: {
        image: {
          src: '/help/intake-studies/list.webp',
          width: 1280,
          height: 720,
        },
        alt: 'Intake Studies with four studies: Fairbanks, Agnes Not started, Carvalho, Ines In progress, and Vasquez, Irene and Navarro, Dolores Complete, with each one\'s dates, unit, room and bed.',
      },
    },
    {
      id: 'new',
      part: 'Start an intake study',
      heading: 'Start a new study and pick the resident',
      text: 'Select New. Pick the resident: type part of the name and choose from the list. Each name shows the birth date, unit, room and diet order, so you pick the right person. Then select OK.',
      media: {
        image: {
          src: '/help/intake-studies/new.webp',
          width: 708,
          height: 336,
        },
        clip: {
          src: '/help/intake-studies/new.mp4',
          width: 708,
          height: 336,
          durationSec: 4.1,
        },
        alt: 'The New Intake Study box with the Resident list open on Kowalski, Lorraine, DOB 12/04/1933, Maple 107-A, Liberal Geriatric, Mechanical Soft, Thin Liquids.',
      },
    },
    {
      id: 'dates',
      part: 'Start an intake study',
      heading: 'Pick the start date and the number of days',
      text: 'The New Intake Study box asks for the dates. Pick the Start Date and the Number of Days (1 to 7; which lengths you can pick is a facility setting). The End Date follows from them.\n\nThe study is built from the menu cycle: every item on the resident\'s tray, for every meal and snack of those days.',
      media: {
        image: {
          src: '/help/intake-studies/dates.webp',
          width: 708,
          height: 532,
        },
        clip: {
          src: '/help/intake-studies/dates.mp4',
          width: 708,
          height: 532,
          durationSec: 5.2,
        },
        alt: 'New Intake Study: Start Date today, Number of Days 3 Days, End Date two days later, and the note This study will be auto-populated from the menu cycle.',
      },
    },
    {
      id: 'save-new',
      part: 'Start an intake study',
      heading: 'Save',
      text: 'Select Save. The study is added to the list and selected, ready to open. It starts with nothing recorded.',
      media: {
        image: {
          src: '/help/intake-studies/save-new.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/intake-studies/save-new.mp4',
          width: 1280,
          height: 720,
          durationSec: 3,
        },
        alt: 'The Intake Studies list with the new study for Kowalski, Lorraine selected at the top, In progress.',
      },
    },
    {
      id: 'open',
      part: 'Record what was eaten',
      heading: 'Open the study',
      text: 'Select the study and then Open, or double-click it. It opens on the resident\'s page, in the Intake section.\n\nPick the Day and the Mealtime at the top (the Date follows the day; the arrows beside it go a day forward or back). Each row is an item from the resident\'s tray for that meal, with its item type, size, consistency and portion. A component, such as margarine with the roll, sits under its item.',
      media: {
        image: {
          src: '/help/intake-studies/open.webp',
          width: 1280,
          height: 454,
        },
        clip: {
          src: '/help/intake-studies/open.mp4',
          width: 1280,
          height: 454,
          durationSec: 2,
        },
        alt: 'Carvalho, Ines\'s intake study open on Day 1, Breakfast: the 0%, 25%, 50%, 75% and 100% boxes beside each item, Orange Juice, Oatmeal and the pureed breakfast items, already recorded.',
      },
    },
    {
      id: 'percent',
      part: 'Record what was eaten',
      heading: 'Mark how much was eaten',
      text: 'Pick the Mealtime, here Lunch. For each item, tick how much the resident ate: 0%, 25%, 50%, 75% or 100%. For another amount, type it in the % box.\n\nSet all rows ticks the same amount on every row of the meal at once: a quick start when most of the tray was eaten. Then change the rows that differ.',
      media: {
        image: {
          src: '/help/intake-studies/percent.webp',
          width: 1280,
          height: 518,
        },
        clip: {
          src: '/help/intake-studies/percent.mp4',
          width: 1280,
          height: 518,
          durationSec: 6,
        },
        alt: 'Day 1 Lunch: every item at 100% except the entree at 50% and the green beans at 25%.',
      },
    },
    {
      id: 'extra',
      part: 'Record what was eaten',
      heading: 'Add an item, or rebuild a meal',
      text: 'Something eaten that was not on the tray, such as a snack from the family? Select Level 2 to add an item to the meal (Level 3 adds one under the selected item), pick it and its size, and mark how much was eaten. Open changes the selected item.\n\nRegenerate rebuilds this day and meal from the menu cycle, for example after a menu change. It replaces the meal\'s rows, so do it before you record.',
      media: {
        image: {
          src: '/help/intake-studies/extra.webp',
          width: 1280,
          height: 518,
        },
        clip: {
          src: '/help/intake-studies/extra.mp4',
          width: 1280,
          height: 518,
          durationSec: 3.3,
        },
        alt: 'The study\'s toolbar: Save, Level 2, Level 3, Level 4, Open, Regenerate and Close.',
      },
    },
    {
      id: 'save',
      part: 'Record what was eaten',
      heading: 'Save, and go on to the next meal',
      text: 'Select Save. Then go on to the next meal, or another day, the same way, and save again.\n\nClose goes back to the resident\'s list of studies. With changes not saved, it asks whether to save them first.',
      media: {
        image: {
          src: '/help/intake-studies/save.webp',
          width: 1280,
          height: 518,
        },
        clip: {
          src: '/help/intake-studies/save.mp4',
          width: 1280,
          height: 518,
          durationSec: 3,
        },
        alt: 'The study after Save, Day 1 Lunch recorded, with the Save button greyed out until the next change.',
      },
    },
    {
      id: 'results',
      part: 'Read the results',
      heading: 'Open the results',
      text: 'Under Clinical, select Nutritional Analysis, then the Intake Study tab. Pick the Resident (only residents with a study are listed), then the Study.\n\nEach item counts its nutrients from its recipe, times the percent eaten.',
      media: {
        image: {
          src: '/help/intake-studies/results.webp',
          width: 1280,
          height: 804,
        },
        clip: {
          src: '/help/intake-studies/results.mp4',
          width: 1280,
          height: 804,
          durationSec: 6,
        },
        alt: 'Nutritional Analysis, Intake Study tab, with Resident Vasquez, Irene and her study of 3 days picked.',
      },
    },
    {
      id: 'needs',
      part: 'Read the results',
      heading: 'Compare what was eaten with the resident\'s needs',
      text: 'At the top are the resident\'s needs per day, from their Calorie/Protein/Fluids section.\n\nThe tiles add up everything eaten over the days recorded. Calories and Protein (and Fluids, when it is one of the nutrients) are checked against the needs times the number of days; the other tiles show the share of the DRI group\'s daily values for those days. Here Irene took in less than her calorie needs over the three days, and enough protein.',
      media: {
        image: {
          src: '/help/intake-studies/needs.webp',
          width: 1280,
          height: 750,
        },
        alt: 'Vasquez, Irene, needs per day Calories 1,700 - 1,900 kcal and Protein 60 - 70 g; eaten over the 3 days recorded: Calories Under its 5,100 - 5,700 target, Protein Within.',
      },
    },
    {
      id: 'by-day',
      part: 'Read the results',
      heading: 'See each day, and each item',
      text: 'By day shows each day\'s total. Items eaten lists every item with its meal, the percent eaten and the nutrients that counted.\n\nA result under the resident\'s needs is something to take to the care team: their meal pattern, snacks and supplements are what you can change. An assessment or the plan of care is where you write it up.',
      media: {
        image: {
          src: '/help/intake-studies/by-day.webp',
          width: 1280,
          height: 246,
        },
        alt: 'By day: Day 1, Day 2 and Day 3 with their dates and totals, and the Complete row with the whole study.',
      },
    },
    {
      id: 'print-menu',
      part: 'Print a study',
      heading: 'Choose what to print',
      text: 'On Intake Studies, select the study, then Print:\n\n- Acceptance prints the study\'s items, meal by meal, with boxes to mark how much was eaten: a sheet for the staff who watch the meals.\n- Intake Study prints the results: what was eaten and its nutrients.\n\nThe same Print is on the resident\'s Intake section.',
      media: {
        image: {
          src: '/help/intake-studies/print-menu.webp',
          width: 1280,
          height: 820,
        },
        clip: {
          src: '/help/intake-studies/print-menu.mp4',
          width: 1280,
          height: 820,
          durationSec: 6,
        },
        alt: 'The Print menu open on Intake Studies with Acceptance and Intake Study.',
      },
    },
    {
      id: 'print-days',
      part: 'Print a study',
      heading: 'Pick the days and print',
      text: 'Pick the days to print; they all start ticked. For Acceptance, check Use entered acceptance percentages to print the amounts already recorded instead of empty boxes.\n\nSelect Print. The report goes to your Print Queue.',
      media: {
        image: {
          src: '/help/intake-studies/print-days.webp',
          width: 708,
          height: 516,
        },
        clip: {
          src: '/help/intake-studies/print-days.mp4',
          width: 708,
          height: 516,
          durationSec: 2,
        },
        alt: 'The Intake Study Acceptance Report Generator box with the three days of the study ticked and Use entered acceptance percentages below them.',
      },
    },
  ],
  tips: [
    'Show all brings back every study the facility has, including discharged residents\' and older ones. The Find box finds a study by the resident\'s name or a date.',
    'A study can also be started from the resident\'s own page: open the resident, then Intake, then New.',
    'Your facility may call intake studies something else, such as Calorie Count. The page and the reports use your facility\'s name for them.',
    'The study\'s rows keep the item, size, consistency and portion the tray ticket served, so the nutrients match what was on the tray.',
  ],
  notes: [
    'This article shows a facility that uses production, where a study is built from the menu cycle. At a facility that does not, the study starts empty: add the items each day by hand, or select Auto-Fill to fill them from the resident\'s meal pattern, then enter how much was eaten.',
    'Which study lengths you can pick (1 to 7 days) is set in your facility settings.',
    'The results count each item\'s nutrients from its recipe, times the percent eaten. An item with no recipe adds nothing; the results say which ones.',
    'Opening and recording need an Intake Study level on your role; New needs a higher one, and Delete (under More actions) higher again. If a button is missing, ask your administrator.',
  ],
  related: [
    'nutritional-analysis-and-validation',
    'nutrition-assessments',
    'entering-weights',
    'meal-patterns',
    'favorites-and-print-queue',
    'finding-a-resident',
  ],
  recorded: '2026-11-03',
};

export default article;
